import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { barValueLabel, formatTrendMonth } from '../../../library/dashboardDisplay';
import { ITrendPoint } from '../../../library/models/clinicDashboard';
import { useSeriesVisibility } from '../../../library/seriesVisibility';
import ResourcesIds from '../../../resources/ResourcesIds';
import { EXISTING_SERIES_COLOR, NEW_SERIES_COLOR, TOTAL_SERIES_COLOR } from '../../../theme/chartPalette';
import { AboveBarLabel } from '../../dashboard/AboveBarLabel';
import { ChartLegend, ChartLegendItem } from '../../dashboard/ChartLegend';
import { ChartScroller } from '../../dashboard/ChartScroller';
import {
    chartCardStyles,
    chartEmptyBoxStyles,
    chartNoteStyles,
    chartSubtitleStyles,
    chartTitleStyles,
} from '../../dashboard/monthlyChart.styles';
import {
    BAR_BORDER_RADIUS,
    BAR_GAP_RATIO,
    CATEGORY_GAP_RATIO,
    chartHeightFor,
    COUNT_TICK_MIN_STEP,
    countAxisMaxWithHeadroom,
    labelFontSizeFor,
    monthTickInterval,
    tickLabelStyleFor,
} from '../../dashboard/monthlyChartConfig';

interface MonthlyPatientsChartProps {
    trend: ITrendPoint[];
}

/**
 * Who the clinic saw, month by month, split into people it had not treated before and
 * people it had.
 *
 * Takes no income mode: a patient count means the same thing whichever way money is
 * counted, and this chart must not so much as flicker when that toggle moves -- the same
 * rule the mode-independent KPI tiles follow.
 *
 * Total is New plus Existing exactly, by construction, so its bar is arithmetically
 * redundant. It stays because it is the bar the eye anchors on when scanning a year.
 */
export const MonthlyPatientsChart = ({ trend }: MonthlyPatientsChartProps): JSX.Element => {
    const theme = useTheme();
    const { t: translate, i18n } = useTranslation();
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

    const months: string[] = trend.map(point => formatTrendMonth(point.month, i18n));

    const allSeries = useMemo(
        () => [
            {
                id: 'TOTAL',
                label: translate(ResourcesIds.DASHBOARD_SERIES_ACTIVE),
                color: TOTAL_SERIES_COLOR,
                data: trend.map(point => point.activePatients),
            },
            {
                id: 'NEW',
                label: translate(ResourcesIds.DASHBOARD_TABLE_NEW),
                color: NEW_SERIES_COLOR,
                data: trend.map(point => point.newPatients),
            },
            {
                id: 'EXISTING',
                label: translate(ResourcesIds.DASHBOARD_TABLE_RETURNING),
                color: EXISTING_SERIES_COLOR,
                data: trend.map(point => point.existingPatients),
            },
        ],
        [trend, translate]
    );

    const seriesIds: string[] = useMemo(() => allSeries.map(series => series.id), [allSeries]);
    const { hidden, toggle } = useSeriesVisibility(seriesIds);

    const visible = allSeries.filter(series => !hidden.has(series.id));
    const legendItems: ChartLegendItem[] = allSeries.map(({ id, label, color }) => ({ id, label, color }));

    const hasPatients: boolean = trend.some(point => point.activePatients > 0);

    return (
        <Card sx={chartCardStyles}>
            <Typography variant="h6" sx={chartTitleStyles}>
                {translate(ResourcesIds.DASHBOARD_CHART_PATIENTS_TITLE)}
            </Typography>

            <Typography variant="caption" sx={chartSubtitleStyles}>
                {translate(ResourcesIds.DASHBOARD_CHART_PATIENTS_SUBTITLE)}
            </Typography>

            {!hasPatients ? (
                <Box sx={chartEmptyBoxStyles}>
                    <Typography variant="body2">
                        {translate(ResourcesIds.DASHBOARD_CHART_EMPTY)}
                    </Typography>
                </Box>
            ) : (
                <>
                    <ChartLegend
                        items={legendItems}
                        hidden={hidden}
                        onToggle={toggle}
                        groupLabel={translate(ResourcesIds.DASHBOARD_LEGEND_LABEL)}
                    />

                    <ChartScroller
                        visibleSeries={visible.length}
                        label={translate(ResourcesIds.DASHBOARD_CHART_PATIENTS_TITLE)}
                    >
                        <BarChart
                            height={chartHeightFor(isNarrow)}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    data: months,
                                    tickLabelInterval: monthTickInterval,
                                    tickLabelStyle: tickLabelStyleFor(isNarrow),
                                    categoryGapRatio: CATEGORY_GAP_RATIO,
                                    barGapRatio: BAR_GAP_RATIO,
                                },
                            ]}
                            // Whole people, on a whole-numbered axis.
                            yAxis={[
                                {
                                    max: countAxisMaxWithHeadroom(visible.flatMap(series => series.data)),
                                    tickMinStep: COUNT_TICK_MIN_STEP,
                                },
                            ]}
                            series={visible}
                            barLabel={barValueLabel(String)}
                            slots={{ barLabel: AboveBarLabel }}
                            slotProps={{ barLabel: { labelFontSize: labelFontSizeFor(isNarrow) } as never }}
                            borderRadius={BAR_BORDER_RADIUS}
                            hideLegend
                        />
                    </ChartScroller>

                    {/* Says the thing a reader would otherwise discover by adding up the
                        bars and finding they exceed the tile above. */}
                    <Typography variant="caption" sx={chartNoteStyles}>
                        {translate(ResourcesIds.DASHBOARD_CHART_ACTIVE_PATIENTS_NOTE)}
                    </Typography>
                </>
            )}
        </Card>
    );
};
